import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { trackAction } from '@/idv/utils';
import type { CaptureKind } from '../../../types';
import type { AutoCaptureKind, CaptureStatus } from '../../camera/types';
type CaptureTelemetryProps = {
  autocaptureKind: AutoCaptureKind;
  /** Only set for identity documents; autocaptureKind already distinguishes selfie and non-id-doc. */
  side?: string;
  status?: CaptureStatus;
};
const useCaptureTelemetry = t0 => {
  const $ = _c(16);
  const {
    autocaptureKind,
    side,
    status
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = Date.now();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const openedAt = useRef(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const statusesSeen = useRef(t2);
  const reachedOk = useRef(false);
  let t3;
  let t4;
  if ($[2] !== autocaptureKind || $[3] !== side || $[4] !== status) {
    t3 = () => {
      if (!status || statusesSeen.current.has(status)) {
        return;
      }
      statusesSeen.current.add(status);
      if (status === "ok") {
        reachedOk.current = true;
      }
      trackAction(`document-capture:status-${status}`, {
        autocaptureKind,
        side
      });
    };
    t4 = [status, autocaptureKind, side];
    $[2] = autocaptureKind;
    $[3] = side;
    $[4] = status;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== autocaptureKind || $[8] !== side) {
    t5 = captureKind => {
      trackAction("document-capture:captured", {
        autocaptureKind,
        side,
        captureKind,
        autoCaptureSucceeded: String(reachedOk.current),
        statusesSeen: Array.from(statusesSeen.current).join(","),
        msSinceOpened: Date.now() - openedAt.current
      });
    };
    $[7] = autocaptureKind;
    $[8] = side;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const reportCapture = t5;
  let t6;
  if ($[10] !== autocaptureKind || $[11] !== side) {
    t6 = () => {
      trackAction("document-capture:camera-stuck", {
        autocaptureKind,
        side,
        statusesSeen: Array.from(statusesSeen.current).join(","),
        msSinceOpened: Date.now() - openedAt.current
      });
    };
    $[10] = autocaptureKind;
    $[11] = side;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const reportCameraStuck = t6;
  let t7;
  if ($[13] !== reportCameraStuck || $[14] !== reportCapture) {
    t7 = {
      reportCapture,
      reportCameraStuck
    };
    $[13] = reportCameraStuck;
    $[14] = reportCapture;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
export default useCaptureTelemetry;